/* Phase Stripe-3 — /admin/churn
 *
 * Distribution of cancellation reasons + KPIs + top notes preview + CSV.
 * Reasons above 25% share are highlighted with the danger tone (churn-alert
 * signal). Threshold value (25% share alert flag) preserved BYTE-EXACT.
 *
 * Wave 6 Batch 3 · Cluster A — Log-Style chrome sweep.
 * Anchor: CreditEngineEvents (Wave 5-Late).
 *
 *   • AdminLayout wrap (unchanged).
 *   • AdminPageHelp lifted out of `actions` (previously mis-nested).
 *   • `_primitives.Stat` replaces the local `KpiCard`; icon+label pattern
 *     preserved.
 *   • `_primitives.Section` shells for reasons table + notes card.
 *   • `_primitives.SelectInput` for range dropdown; `GhostButton`/`AccentButton`
 *     for Refresh/CSV.
 *   • Distribution bar tokens: `--ds-color-accent` vs `--ds-color-danger`.
 *   • Dense hairline table + `isLast` guard.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Download, AlertTriangle, TrendingDown, Clock, Award } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Section, Stat, SelectInput, GhostButton, AccentButton } from "@/admin/_primitives";

function pct(v) {
  return `${((Number(v) || 0) * 100).toFixed(1)}%`;
}

export default function ChurnAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/churn", { params: { range } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  async function exportCsv() {
    setExporting(true);
    try {
      const res = await api.get("/admin/churn", {
        params: { range, export_csv: true },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `churn-${range}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  }

  const kpis = data?.kpis || {};
  const reasons = data?.reasons || [];
  const notesByReason = data?.notes_by_reason || {};
  const maxCount = Math.max(1, ...reasons.map((r) => r.count || 0));

  return (
    <AdminLayout
      title="Churn Reasons"
      subtitle="Cancellation reasons captured from the retention flow. Reasons above 25% share are flagged as alerts."
      actions={
        <div className="flex items-center gap-2">
          <SelectInput
            data-testid="admin-churn-range"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            aria-label="Time range"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </SelectInput>
          <GhostButton
            data-testid="admin-churn-refresh"
            onClick={load}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </GhostButton>
          <AccentButton
            data-testid="admin-churn-export"
            onClick={exportCsv}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            CSV
          </AccentButton>
        </div>
      }
    >
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="Churn"
          purpose="Cancellation events, reasons cited, and voluntary/involuntary churn rates."
          configures="Nothing — read-only. Filter by cancellation reason and time window."
          impact="No user-facing impact. Used for churn analysis and retention initiatives."
          safety="Read-only. Records are appended when Stripe reports a subscription cancellation."
        />
      </div>

      {loading && !data ? (
        <div
          className="text-center py-16 text-sm"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading churn data…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <Stat
              testid="admin-churn-kpi-cancellations"
              label={`Cancellations · ${range}`}
              value={kpis.cancellations_in_range ?? 0}
              icon={TrendingDown}
            />
            <Stat
              testid="admin-churn-kpi-avg-tenure"
              label="Avg tenure at cancel (days)"
              value={kpis.avg_tenure_days_at_cancel ?? 0}
              icon={Clock}
            />
            <Stat
              testid="admin-churn-kpi-founding"
              label="Founding member cancellations"
              value={kpis.cancellations_by_founding_members ?? 0}
              icon={Award}
            />
          </div>

          <Section
            title="Cancellation reasons"
            testid="admin-churn-reasons-section"
            bodyClassName="p-0"
          >
            <table className="w-full text-[13px]" data-testid="admin-churn-reasons-table">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2">Reason</th>
                  <th className="px-4 py-2 text-right">Count</th>
                  <th className="px-4 py-2 text-right">Share</th>
                  <th className="px-4 py-2 text-right">Avg tenure</th>
                  <th className="px-4 py-2">Distribution</th>
                </tr>
              </thead>
              <tbody>
                {reasons.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No cancellations in this range yet. That's a good problem to have.
                    </td>
                  </tr>
                ) : (
                  reasons.map((r, idx) => {
                    const isLast = idx === reasons.length - 1;
                    return (
                      <tr
                        key={r.reason_code}
                        data-testid={`admin-churn-row-${r.reason_code}`}
                        style={{
                          borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                        }}
                      >
                        <td className="px-4 py-1.5" style={{ color: "var(--ds-color-ink)" }}>
                          <div className="flex items-center gap-2">
                            {r.is_alert && (
                              <AlertTriangle
                                className="w-3.5 h-3.5"
                                style={{ color: "var(--ds-color-danger)" }}
                                aria-hidden="true"
                              />
                            )}
                            <span>{r.reason_label}</span>
                          </div>
                        </td>
                        <td
                          className="px-4 py-1.5 text-right tabular-nums font-mono"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          {r.count}
                        </td>
                        <td
                          className="px-4 py-1.5 text-right tabular-nums font-mono"
                          style={{
                            color: r.is_alert
                              ? "var(--ds-color-danger)"
                              : "var(--ds-color-ink-muted)",
                          }}
                        >
                          <span data-testid={`admin-churn-share-${r.reason_code}`}>{pct(r.share)}</span>
                        </td>
                        <td
                          className="px-4 py-1.5 text-right tabular-nums font-mono"
                          style={{ color: "var(--ds-color-ink-muted)" }}
                        >
                          {r.avg_tenure_days} d
                        </td>
                        <td className="px-4 py-1.5">
                          <div
                            className="h-2 rounded-full overflow-hidden"
                            style={{ background: "var(--ds-color-surface-muted)" }}
                          >
                            <div
                              className="h-full"
                              style={{
                                width: `${(r.count / maxCount) * 100}%`,
                                background: r.is_alert
                                  ? "var(--ds-color-danger)"
                                  : "var(--ds-color-accent)",
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Section>

          {Object.keys(notesByReason).length > 0 && (
            <div className="mt-3">
              <Section title="Recent free-text notes (per reason, last 3)" testid="admin-churn-notes-section">
                <div className="space-y-4">
                  {Object.entries(notesByReason).map(([code, notes]) => (
                    <div key={code} data-testid={`admin-churn-notes-${code}`}>
                      <div
                        className="text-xs font-semibold mb-2"
                        style={{ color: "var(--ds-color-ink)" }}
                      >
                        {reasons.find((r) => r.reason_code === code)?.reason_label || code}
                      </div>
                      <ul className="space-y-2">
                        {notes.map((n, i) => (
                          <li
                            key={i}
                            className="text-[11px] italic pl-3 border-l-2"
                            style={{
                              color: "var(--ds-color-ink-muted)",
                              borderColor: "var(--ds-color-admin-border)",
                            }}
                          >
                            "{n.reason_text || n.additional_notes || "(no note)"}"
                            <span
                              className="not-italic ml-2 text-[10px]"
                              style={{ color: "var(--ds-color-ink-subtle)" }}
                            >
                              · tenure {n.tenure_days} d · {new Date(n.submitted_at).toLocaleDateString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
